import React, { useCallback, useEffect } from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { getConstanceSettings } from '@disco/state-management/selectors/user'

import {
  getBrief,
  loadBriefRecipients,
  getBriefActiveRecipientEmails,
} from '@disco/state-management/reducers/entities/briefs'
import DocumentHeadTags from 'components/document-head-tags'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { ShareBriefModal } from '../../components/share-brief-modal'
import { useReopenBrief } from '../../hooks/use-reopen-brief'
import { updateBriefInPanes } from '@disco/state-management/actions/ExplorerActions'

const BriefSharePage: React.FC = () => {
  const dispatch = useDispatch()
  const params = useParams<{ briefId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const constanceSettings = useSelector(getConstanceSettings)
  const briefWorkflowV2Enabled = constanceSettings?.BRIEF_WORKFLOW_V2 || false

  const briefId = parseInt(params.briefId)

  useEffect(() => {
    dispatch(loadBriefRecipients(briefId))
  }, [dispatch, briefId])

  const brief = useSelector(state => getBrief(state, { briefId }))

  const activeRecipients = useSelector(state =>
    getBriefActiveRecipientEmails(state, { briefId }),
  )

  const { handleReopenBrief } = useReopenBrief({
    brief,
    onSuccess: updatedBrief => {
      dispatch(updateBriefInPanes(updatedBrief))
    },
  })

  // Redirect to editor if brief is closed in V2 workflow
  useEffect(() => {
    if (brief && briefWorkflowV2Enabled && brief.is_open === false) {
      navigate(`/brief-editor-v2/${brief.id}`)
    }
  }, [brief, briefWorkflowV2Enabled, navigate])

  const returnTo = searchParams.get('return')

  const manageAccessPath = location.pathname.includes('brief-editor-v2')
    ? `/brief-editor-v2/${brief?.id}/access`
    : `/briefs/${brief?.id}/access`

  const basePath = location.pathname.includes('brief-editor-v2')
    ? `/brief-editor-v2/${brief?.id}`
    : '/briefs'

  const handleCloseModal = useCallback(() => {
    if (returnTo) {
      return navigate(returnTo, { replace: true })
    }

    navigate(basePath)
  }, [returnTo, navigate, basePath])

  const handleManageBriefAccess = () => {
    navigate(manageAccessPath, {
      state: { from: location.pathname },
    })
  }

  const canShareBrief = brief && brief.publishing_status !== 'draft'

  return (
    <div>
      <DocumentHeadTags title="Share brief | DISCO" />
      {canShareBrief && (
        <ShareBriefModal
          onClose={handleCloseModal}
          brief={brief}
          initialRecipients={location?.state?.initialRecipients}
          allRecipients={activeRecipients}
          handleReopenBrief={handleReopenBrief}
          onManageAccess={handleManageBriefAccess}
        />
      )}
    </div>
  )
}

export { BriefSharePage }
export default BriefSharePage
